/**
 * Authentication Monitoring and Logging Service
 * Implements comprehensive monitoring for security and debugging
 */

import { FirebaseError } from 'firebase/app';
import { debugLog } from '../utils/debugLog';

interface AuthEvent {
  type: 'login_attempt' | 'login_success' | 'login_failure' | 'rate_limit_triggered' | 'token_refresh' | 'session_expired';
  timestamp: number;
  userIdentifier?: string; // Hashed or sanitized identifier
  errorCode?: string;
  context?: string;
  metadata?: Record<string, any>;
}

interface SecurityMetrics {
  totalLoginAttempts: number;
  successfulLogins: number;
  failedLogins: number;
  rateLimitTriggers: number;
  tokenRefreshes: number;
  sessionsExpired: number;
  uniqueUsers: Set<string>;
  errorBreakdown: Record<string, number>;
}

class AuthMonitoringService {
  private events: AuthEvent[] = [];
  private readonly maxEvents = 1000; // Keep last 1000 events in memory
  private metrics: SecurityMetrics = {
    totalLoginAttempts: 0,
    successfulLogins: 0,
    failedLogins: 0,
    rateLimitTriggers: 0,
    tokenRefreshes: 0,
    sessionsExpired: 0,
    uniqueUsers: new Set(),
    errorBreakdown: {}
  };

  /**
   * Log authentication events for monitoring
   */
  logAuthEvent(event: Omit<AuthEvent, 'timestamp'>): void {
    const authEvent: AuthEvent = {
      ...event,
      timestamp: Date.now()
    };

    // Add to events list (keep only recent events)
    this.events.push(authEvent);
    if (this.events.length > this.maxEvents) {
      this.events.shift();
    }

    // Update metrics
    this.updateMetrics(authEvent);

    // Log to appropriate destinations
    this.logToDestinations(authEvent);
  }

  /**
   * Log login attempt
   */
  logLoginAttempt(email: string, context?: string): void {
    const userHash = this.hashIdentifier(email);
    this.logAuthEvent({
      type: 'login_attempt',
      userIdentifier: userHash,
      context,
      metadata: {
        source: 'user_initiated'
      }
    });
  }

  /**
   * Log successful login
   */
  logLoginSuccess(email: string, context?: string): void {
    const userHash = this.hashIdentifier(email);
    this.logAuthEvent({
      type: 'login_success',
      userIdentifier: userHash,
      context,
      metadata: {
        source: 'auth_success'
      }
    });
  }

  /**
   * Log failed login with Firebase error
   */
  logLoginFailure(email: string, error: FirebaseError, context?: string): void {
    const userHash = this.hashIdentifier(email);
    this.logAuthEvent({
      type: 'login_failure',
      userIdentifier: userHash,
      errorCode: error.code,
      context,
      metadata: {
        source: 'auth_failure',
        isCritical: this.isCriticalError(error),
        errorMessage: this.sanitizeErrorMessage(error.message)
      }
    });
  }

  /**
   * Log rate limit trigger
   */
  logRateLimitTrigger(email: string, operation: string, attemptsCount: number): void {
    const userHash = this.hashIdentifier(email);
    this.logAuthEvent({
      type: 'rate_limit_triggered',
      userIdentifier: userHash,
      context: operation,
      metadata: {
        attempts: attemptsCount,
        severity: attemptsCount > 10 ? 'high' : 'medium'
      }
    });
  }

  /**
   * Log token refresh events
   */
  logTokenRefresh(email: string, success: boolean, reason?: string): void {
    const userHash = this.hashIdentifier(email);
    this.logAuthEvent({
      type: 'token_refresh',
      userIdentifier: userHash,
      context: reason || 'automatic',
      metadata: {
        success,
        source: 'apollo_client'
      }
    });
  }

  /**
   * Log session expiry events
   */
  logSessionExpiry(reason: string, source?: string): void {
    this.logAuthEvent({
      type: 'session_expired',
      context: reason,
      metadata: {
        source: source || 'unknown',
        automatic: true
      }
    });
  }

  /**
   * Get current security metrics
   */
  getMetrics(): SecurityMetrics & { eventsCount: number } {
    return {
      ...this.metrics,
      eventsCount: this.events.length
    };
  }

  /**
   * Get recent events (for debugging)
   */
  getRecentEvents(count: number = 50): AuthEvent[] {
    return this.events.slice(-count);
  }

  /**
   * Get events by type
   */
  getEventsByType(type: AuthEvent['type'], limit: number = 100): AuthEvent[] {
    return this.events
      .filter(event => event.type === type)
      .slice(-limit);
  }

  /**
   * Check for suspicious activity patterns
   */
  detectSuspiciousActivity(): {
    hasSuspiciousActivity: boolean;
    alerts: string[];
    recommendations: string[];
  } {
    const alerts: string[] = [];
    const recommendations: string[] = [];
    const recentEvents = this.getRecentEvents(100);
    const lastHour = Date.now() - (60 * 60 * 1000);

    // Check for rapid failed login attempts
    const recentFailures = recentEvents.filter(
      e => e.type === 'login_failure' && e.timestamp > lastHour
    );

    if (recentFailures.length > 20) {
      alerts.push(`High number of failed login attempts: ${recentFailures.length} in the last hour`);
      recommendations.push('Consider implementing CAPTCHA or additional rate limiting');
    }

    // Check for token refresh failures
    const tokenRefreshFailures = recentEvents.filter(
      e => e.type === 'token_refresh' && e.metadata?.success === false && e.timestamp > lastHour
    );

    if (tokenRefreshFailures.length > 10) {
      alerts.push(`Multiple token refresh failures: ${tokenRefreshFailures.length} in the last hour`);
      recommendations.push('Check Firebase token configuration and network connectivity');
    }

    // Check for multiple rate limit triggers
    const rateLimitTriggers = recentEvents.filter(
      e => e.type === 'rate_limit_triggered' && e.timestamp > lastHour
    );

    if (rateLimitTriggers.length > 5) {
      alerts.push(`Multiple rate limit triggers: ${rateLimitTriggers.length} in the last hour`);
      recommendations.push('Review rate limiting configuration and monitor for potential attacks');
    }

    return {
      hasSuspiciousActivity: alerts.length > 0,
      alerts,
      recommendations
    };
  }

  /**
   * Export metrics for external monitoring systems
   */
  exportMetrics(): Record<string, number | string> {
    const metrics = this.getMetrics();
    const suspicious = this.detectSuspiciousActivity();

    return {
      'auth.login_attempts.total': metrics.totalLoginAttempts,
      'auth.login_success.total': metrics.successfulLogins,
      'auth.login_failure.total': metrics.failedLogins,
      'auth.rate_limits.total': metrics.rateLimitTriggers,
      'auth.token_refresh.total': metrics.tokenRefreshes,
      'auth.session_expired.total': metrics.sessionsExpired,
      'auth.unique_users.total': metrics.uniqueUsers.size,
      'auth.success_rate': metrics.totalLoginAttempts > 0 
        ? (metrics.successfulLogins / metrics.totalLoginAttempts * 100).toFixed(2)
        : '0',
      'auth.suspicious_activity': suspicious.hasSuspiciousActivity ? 1 : 0,
      'auth.alerts_count': suspicious.alerts.length
    };
  }

  /**
   * Clear old data and reset metrics (maintenance function)
   */
  clearOldData(olderThanMs: number = 24 * 60 * 60 * 1000): void {
    const cutoff = Date.now() - olderThanMs;
    this.events = this.events.filter(event => event.timestamp > cutoff);
    
    // Reset metrics that should be recalculated
    this.metrics.uniqueUsers.clear();
    this.metrics.errorBreakdown = {};
    
    // Recalculate metrics from remaining events
    this.events.forEach(event => this.updateMetrics(event));
  }

  /**
   * Private: Update metrics based on event
   */
  private updateMetrics(event: AuthEvent): void {
    switch (event.type) {
      case 'login_attempt':
        this.metrics.totalLoginAttempts++;
        break;
      case 'login_success':
        this.metrics.successfulLogins++;
        if (event.userIdentifier) {
          this.metrics.uniqueUsers.add(event.userIdentifier);
        }
        break;
      case 'login_failure':
        this.metrics.failedLogins++;
        if (event.errorCode) {
          this.metrics.errorBreakdown[event.errorCode] = 
            (this.metrics.errorBreakdown[event.errorCode] || 0) + 1;
        }
        break;
      case 'rate_limit_triggered':
        this.metrics.rateLimitTriggers++;
        break;
      case 'token_refresh':
        this.metrics.tokenRefreshes++;
        break;
      case 'session_expired':
        this.metrics.sessionsExpired++;
        break;
    }
  }

  /**
   * Private: Hash email for privacy
   */
  private hashIdentifier(email: string): string {
    // Simple hash for privacy (in production, use crypto.subtle or similar)
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      const char = email.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `user_${Math.abs(hash).toString(36)}`;
  }

  /**
   * Private: Check if error is critical
   */
  private isCriticalError(error: FirebaseError): boolean {
    const criticalErrors = [
      'auth/internal-error',
      'auth/operation-not-allowed',
      'auth/service-unavailable',
      'auth/user-disabled'
    ];
    return criticalErrors.includes(error.code);
  }

  /**
   * Private: Sanitize error message for logging
   */
  private sanitizeErrorMessage(message: string): string {
    // Remove potential sensitive information
    return message
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
      .replace(/\b\d{4,}\b/g, '[NUMBERS]')
      .substring(0, 200); // Limit length
  }

  /**
   * Private: Log to various destinations
   */
  private logToDestinations(event: AuthEvent): void {
    // Console logging (development)
    if (process.env.NODE_ENV === 'development') {
      debugLog(`[AUTH] ${event.type}:`, event);
    }

    // Production logging
    if (process.env.NODE_ENV === 'production') {
      // Send to external monitoring service
      this.sendToMonitoringService(event);
    }

    // Critical events should always be logged
    if (event.metadata?.isCritical || event.type === 'rate_limit_triggered') {
      this.sendCriticalAlert(event);
    }
  }

  /**
   * Private: Send to external monitoring service
   */
  private sendToMonitoringService(event: AuthEvent): void {
    // Example integration points:
    // - Sentry: Sentry.addBreadcrumb({ category: 'auth', message: event.type, data: event });
    // - LogRocket: LogRocket.track('auth_event', event);
    // - Custom analytics: analytics.track('auth_event', event);
    
    // For now, just console.warn for visibility
    if (event.type === 'login_failure' || event.type === 'rate_limit_triggered') {
      console.warn(`[AUTH MONITORING] ${event.type}:`, {
        type: event.type,
        errorCode: event.errorCode,
        context: event.context,
        timestamp: new Date(event.timestamp).toISOString()
      });
    }
  }

  /**
   * Private: Send critical alerts
   */
  private sendCriticalAlert(event: AuthEvent): void {
    // In production, this would integrate with alerting systems
    // - Slack webhooks
    // - Email alerts
    // - SMS alerts for critical security events
    
    console.error(`[CRITICAL AUTH EVENT] ${event.type}:`, event);
  }
}

// Singleton instance
export const authMonitoringService = new AuthMonitoringService();

// Auto-cleanup old data every hour
if (typeof window !== 'undefined') {
  setInterval(() => {
    authMonitoringService.clearOldData();
  }, 60 * 60 * 1000); // 1 hour
}

// Export metrics to window for debugging (development only)
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as any).authMetrics = {
    getMetrics: () => authMonitoringService.getMetrics(),
    getRecentEvents: (count?: number) => authMonitoringService.getRecentEvents(count),
    detectSuspicious: () => authMonitoringService.detectSuspiciousActivity(),
    exportMetrics: () => authMonitoringService.exportMetrics()
  };
}